import React, { useState } from "react";
import { supabase } from "../../../services/supabaseClient";

type SearchCitationCodesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectCitationCode: (code: any) => void;
};

function SearchCitationCodesModal({
  isOpen,
  onClose,
  onSelectCitationCode,
}: SearchCitationCodesModalProps) {
  const [results, setResults] = useState(() => []);
  const [citationCode, setCitationCode] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSearch = async () => {
    setLoading(true);
    try {
      // ✅ Query citation_codes table
      let query = supabase
        .from("citation_codes")
        .select(
          "id, citation_code, citation_code_description, applies_to_gcc, applies_to_cpc, is_exclusion_code, status"
        )
        .eq("status", "active")
        .order("citation_code");

      if (citationCode.trim()) {
        query = query.ilike("citation_code", `%${citationCode.trim()}%`);
      }

      if (description.trim()) {
        query = query.ilike("citation_code_description", `%${description.trim()}%`);
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error("Citation codes search error:", error);
        setResults([]);
      } else {
        setResults(data || []);
      }
    } catch (err) {
      console.error("Unexpected search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-modal-overlay" onClick={onClose}>
      <div className="data-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-modal-header">
          <h2 className="data-modal-title">Search Citation Codes</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="data-modal-body">
          <div className="form-grid">
            {/* Citation Code */}
            <div className="form-group">
              <label className="form-label">Citation Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by citation code..."
                value={citationCode}
                onChange={(e) => setCitationCode(e.target.value)}
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Search Button */}
            <div className="form-group">
              <button
                className="modal-btn modal-btn-save"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* Instruction Line */}
          <div
            style={{
              fontSize: "0.9em",
              margin: "10px 0 6px 0",
              color: "#444",
            }}
          >
            Click <strong>Use This Citation Code</strong> to select and confirm a
            citation that applies to this test report.
          </div>

          {/* Results */}
          <div
            className="results-list"
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {results.length === 0 ? (
              <div>No citation codes found</div>
            ) : (
              results.map((code: any) => (
                <div
                  key={`citation-${code.id}`}
                  className="citation-result"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    padding: "8px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div>
                    <strong>{code.citation_code}</strong>
                    <div style={{ fontSize: "0.9em", color: "#555" }}>
                      {code.citation_code_description || "No description"}
                      <br />
                      Applies to:{" "}
                      {[
                        code.applies_to_cpc ? "CPC" : null,
                        code.applies_to_gcc ? "GCC" : null,
                      ]
                        .filter(Boolean)
                        .join(", ") || "None"}
                      {code.is_exclusion_code ? " — Exclusion Code" : ""}
                    </div>
                  </div>

                  {/* Use This Citation Code Button */}
                  <button
                    className="modal-btn modal-btn-save"
                    style={{
                      alignSelf: "flex-start",
                      fontSize: "0.8em",
                      padding: "4px 10px",
                      marginTop: "2px",
                    }}
                    onClick={() => {
                      onSelectCitationCode(code);
                      onClose();
                    }}
                  >
                    Use This Citation Code
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="data-modal-footer">
          <button className="modal-btn modal-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default SearchCitationCodesModal;
